import { useState } from 'react'

import { toaster } from '@/components/Toaster'

import { useAddTag, useTags } from '../api'
import { AssetTag } from '../components/TableHeaderTags/TableHeaderTags.types'
import { useTagsStore } from '../store'

interface UseTagCreateOptions {
  onSuccess?: (newTag: AssetTag) => void
  onError?: (error: unknown) => void
}

/**
 * Hook to handle tag creation functionality with API integration
 * @param options Configuration options
 * @returns Object containing tag creation state and functions
 */
export const useTagCreate = (options: UseTagCreateOptions = {}) => {
  const { onSuccess, onError } = options
  const [isCreating, setIsCreating] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const { trigger: addTag, isMutating } = useAddTag()
  const { globalOrgTags, setGlobalOrgTags } = useTagsStore()
  const { mutate: refetchTags } = useTags({ page: 1, pageSize: 100 })

  const createTag = async (name: string, color: string) => {
    const trimmedName = name.trim()
    if (!trimmedName || !color) return null

    // Reset validation error
    setValidationError(null)

    // Check minimum length requirement
    if (trimmedName.length < 3) {
      setValidationError('Tag name must be at least 3 characters')
      return
    }

    // Check if tag name already exists in available tags
    const tagExists = globalOrgTags.some(
      (tag) => tag.name.toLowerCase() === trimmedName.toLowerCase(),
    )

    if (tagExists) {
      const error = `A tag with the name "${trimmedName}" already exists`
      setValidationError(error)

      return
    }

    setIsCreating(true)

    try {
      const newTag = await addTag({ name: trimmedName, color })

      // After successful addTag, update local store
      setGlobalOrgTags([...globalOrgTags, newTag])

      // Refetch in background to keep the list consistent with the backend
      refetchTags()

      onSuccess?.(newTag)
    } catch (error) {
      console.error('Failed to create tag:', error)
      toaster({ message: 'Failed to create tag', type: 'error' })
      onError?.(error)
    } finally {
      setIsCreating(false)
    }
  }

  return {
    createTag,
    isCreating: isCreating || isMutating,
    validationError,
  }
}
